import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

interface LoginProps {
  onLogin: (role: string) => void;
}

export const Login = ({ onLogin }: LoginProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false); // New state for loading indicator
  const navigate = useNavigate();

  /**
   * Authenticates the user by making an API call to the backend login endpoint.
   * @param userEmail The email entered by the user.
   * @param userPassword The password entered by the user.
   * @returns A Promise that resolves with the user's role on successful login, or rejects with an error message.
   */
  const authenticateUser = async (userEmail: string, userPassword: string): Promise<string> => {
    const url = "https://interim-cabdemo-module.ispgnet.com/api/login"; // Your backend API login endpoint

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: userEmail, password: userPassword }),
      });

      if (response.ok) { // Check if the response status is 2xx (success)
        const data = await response.json();
        // Assuming your backend returns a 'role' field upon successful login.
        // If your API doesn't return a 'role', you'll need to infer it based on the email
        // or have the backend provide it. For now, we'll infer if not provided.
        let userRole: string;
        if (data && data.role) {
          userRole = data.role;
        } else {
          // Fallback: Infer role based on email if backend doesn't explicitly return it
          if (userEmail === "autoconnectai@oorjit.com") {
            userRole = "admin";
          } else if (userEmail === "almajdouie@oorjit.com") {
            userRole = "almajdouie";
          } else if (userEmail === "leadassistance@oorjit.com") {
            userRole = "leadAssistance"; // Example for a test user
          } else {
            // This case should ideally not be reached if backend authenticates correctly
            throw new Error("Authentication successful, but user role could not be determined.");
          }
        }
        return userRole;
      } else {
        // Handle non-2xx responses (e.g., 401 Unauthorized, 400 Bad Request)
        const errorData = await response.json();
        // Changed from errorData.message to errorData.detail to match your backend's error structure
        throw new Error(errorData.detail || "Authentication failed. Please check your credentials.");
      }
    } catch (error) {
      // Handle network errors or other exceptions during the fetch call
      console.error("Login API call failed:", error);
      // Ensure the error message is always a string
      if (error instanceof Error) {
        throw new Error(error.message); // Use the error message if it's an Error object
      } else {
        throw new Error("An unexpected error occurred during login."); // Generic message for unknown errors
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true); // Set loading to true

    try {
      // Call the actual backend authentication
      const userRole = await authenticateUser(email, password);

      // If authentication is successful, proceed with login
      onLogin(userRole);

      // Navigate based on the user's role
      if (userRole === "admin") {
        navigate("/");
      } else if (userRole === "almajdouie") {
        navigate("/Almajdouie_Analytics");
      }
      else if (userRole === "leadAssistance") {
        navigate("/Lead_CallAnalytics");
      }
    } catch (err: any) {
      // Handle authentication errors
      // Ensure err is converted to a string before setting the state
      let errorMessage = "An unknown error occurred.";
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage); // Always set a string
    } finally {
      setIsLoading(false); // Set loading to false regardless of success or failure
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo Container */}
        <div className="logo text-center">
          <img
            className="mx-auto" // Adjust size as needed
            src="./oorjit-logo.png" // or use the imported version
            alt="Company Logo"
        
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        
        {/* <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
        Login to your account
        </h2> */}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-[540px]">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="block text-lg text-center font-medium text-gray-700">
              <h1>Login to your account</h1>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-black-500 focus:border-black-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-black-500 focus:border-black-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <a href="#" className="font-medium text-black-600 hover:text-black-500">
                  Forgot password?
                </a>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700"
              >
                Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};